import { ListItem, ListItemButton, ListItemIcon, styled } from '@mui/material';

import { useNavigate, useParams } from 'react-router';

import { useState } from 'react';

import { TAppraisalGroupSummaryDto } from '@app-velocity/av-api-v2';
import { ArrowForwardIcon, ClearIcon, FrameIcon } from '@app-velocity/icons';

import { MessageBus } from '@app-velocity/message-bus';

import { DeferredImage } from '@app-velocity/ui';

import { useAppraisalListContext } from '../../hooks/useAppraisalListContext';
import { AppraisalsRoutePaths } from '../../../../routes/AppraisalsRoutePaths';
import { SingelAppraisalItemText } from './components/SingleAppraisalItemText';

export type SingleAppraisalProps = {
  appraisal: TAppraisalGroupSummaryDto;
};

const StyledAppraisalIcon = styled(ListItemIcon)(({ theme }) => ({
  height: theme.spacing(16),
  width: theme.spacing(12),
  padding: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: theme.spacing(1),
  background: 'var(--background-background-secondary-color)',
  marginRight: theme.spacing(2),
  img: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  svg: {
    fill: 'var(--icon-icon-secondary-color)',
  },
}));

const StyledAppraisalButton = styled('button')({
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: '0 3px',
});

const StyledOpenButton = styled(StyledAppraisalButton)<{ hovered: boolean }>(
  ({ hovered }) => ({
    svg: {
      fill: hovered
        ? 'var(--icon-icon-secondary-color)'
        : 'var(--icon-icon-tertiary-color)',
    },
  })
);

const StyledDeleteButton = styled(StyledAppraisalButton)<{ hovered: boolean }>(
  ({ hovered }) => ({
    svg: {
      fill: hovered ? 'var(--error-error-icon-primary-color)' : 'transparent',
    },
  })
);

const StyledListItem = styled(ListItem)(({ theme }) => ({
  padding: 0,
  margin: theme.spacing(-1.5),
  width: 'auto',
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  padding: theme.spacing(1.5),
}));

const messageBus = new MessageBus('system');

export const SingleAppraisal = ({ appraisal }: SingleAppraisalProps) => {
  const navigate = useNavigate();
  const { teamId, workspaceId } = useParams<{
    teamId: string;
    workspaceId: string;
  }>();
  const [hovered, setHovered] = useState(false);
  const { removeAppraisal } = useAppraisalListContext();

  const confirmAppraisalDelete = (e: React.MouseEvent) => {
    e.stopPropagation();

    messageBus.publish('modal:action-confirmation:open', {
      title: 'appraisals:delete.title',
      description: 'appraisals:delete.description',
      confirmButtonText: 'shared:delete',
      cancelButtonText: 'shared:cancel',
      confirmButtonColor: 'error',
      onModalCloseCallback: (decision) => {
        if (decision === 'CANCEL') return;

        removeAppraisal(appraisal.id!);
      },
    });
  };

  const goToDetails = () => {
    if (!workspaceId || !teamId || !appraisal.id) return;
    navigate(
      AppraisalsRoutePaths.appraisalDetails(teamId, workspaceId, appraisal.id)
    );
  };

  return (
    <StyledListItem>
      <StyledListItemButton
        onClick={goToDetails}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <StyledAppraisalIcon>
          {appraisal.nodeImage ? (
            <DeferredImage
              src={appraisal.nodeImage}
              alt={appraisal.nodeId || ''}
            />
          ) : (
            <FrameIcon />
          )}
        </StyledAppraisalIcon>
        <SingelAppraisalItemText appraisal={appraisal} />
        <StyledDeleteButton
          type="button"
          onClick={confirmAppraisalDelete}
          hovered={hovered}
        >
          <ClearIcon />
        </StyledDeleteButton>
        <StyledOpenButton type="button" onClick={goToDetails} hovered={hovered}>
          <ArrowForwardIcon />
        </StyledOpenButton>
      </StyledListItemButton>
    </StyledListItem>
  );
};
